import { motion } from "framer-motion";

type Hotel = {
  name: string;
  location: string;
  /** Short vibe/description — one or two sentences. */
  description: string;
  /** Discount headline, e.g. "20% OFF". */
  discount: string;
  /** Direct booking link (opens in a new tab). */
  bookHref: string;
  /** Optional autoplay video (muted/looped). Falls back to `image` if omitted. */
  video?: string;
  /** Poster/fallback image. Used as the video poster and when no video is set. */
  image: string;
  alt: string;
  /** Optional one-line tip shown under the description, e.g. how to reveal the rate. */
  rateTip?: string;
};

/**
 * Partner hotels offering a Yacht Rock Detroit rate.
 * Add a future hotel by dropping another object in here — the grid handles the rest.
 */
const hotels: Hotel[] = [
  {
    name: "The Siren Hotel",
    location: "Downtown Detroit · ~10 min to the venue",
    description:
      "A romantic boutique hotel inside the historic 1926 Wurlitzer Building — retro-modern rooms, the pink-velvet Candy Bar, and Albena downstairs. One of Detroit's most beautiful places to stay.",
    discount: "20% OFF",
    bookHref: "https://reservations.travelclick.com/115157?RatePlanId=11410501",
    video: "/hotels/siren-flower-arranging.mp4",
    image: "/hotels/siren-candy-bar.webp",
    alt: "The Siren Hotel Detroit — the pink velvet Candy Bar lounge",
    rateTip: "Pick your dates, then tap “More Rates” under any room to unlock the Roostertail rate — no blackout dates.",
  },
];

const HotelPartners = () => {
  return (
    <div className="flex flex-wrap justify-center gap-8 md:gap-10">
      {hotels.map((hotel, i) => (
        <motion.article
          key={hotel.name}
          className="group w-full max-w-md flex flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Media */}
          <div className="relative aspect-[4/3] overflow-hidden bg-yacht-charcoal/5">
            {hotel.video ? (
              <video
                src={hotel.video}
                poster={hotel.image}
                loop
                muted
                playsInline
                autoPlay
                aria-label={hotel.alt}
                className="h-full w-full object-cover"
              />
            ) : (
              <img
                src={hotel.image}
                alt={hotel.alt}
                loading="lazy"
                className="h-full w-full object-cover"
              />
            )}

            {/* Discount badge */}
            <div className="absolute left-4 top-4 z-10">
              <span className="inline-flex items-center rounded-full bg-yacht-yellow px-4 py-1.5 text-sm font-bold uppercase tracking-wide text-yacht-charcoal shadow-lg">
                {hotel.discount}
              </span>
            </div>
          </div>

          {/* Body */}
          <div className="flex flex-1 flex-col p-6 md:p-8">
            <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.3em] text-yacht-blue">
              Partner Hotel
            </p>
            <h3 className="text-2xl font-bold text-yacht-charcoal md:text-3xl">
              {hotel.name}
            </h3>
            <p className="mt-1 text-sm font-medium text-yacht-charcoal/50">
              {hotel.location}
            </p>
            <p
              className="mt-4 text-base leading-relaxed text-yacht-charcoal/70"
              style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
            >
              {hotel.description}
            </p>

            {hotel.rateTip && (
              <p className="mt-4 border-l-2 border-yacht-yellow pl-3 text-sm italic leading-relaxed text-yacht-charcoal/60">
                {hotel.rateTip}
              </p>
            )}

            <div className="mt-6 flex-1" />

            <a
              href={hotel.bookHref}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center rounded-full bg-yacht-blue px-8 py-4 text-base font-bold text-white shadow-lg transition-transform hover:scale-[1.02] hover:bg-yacht-blue/90"
            >
              Book Now
            </a>
          </div>
        </motion.article>
      ))}
    </div>
  );
};

export default HotelPartners;
